import React from 'react';
import type { C3Entity } from '../../mock/c3';
import Dialog from '../../ui/Dialog';
import { useToast } from '../../app/ToastProvider';
import { scoreCls, scoreColor as scoreColorVar } from './eriUtils';

interface EntityDossierModalProps {
  entity: C3Entity;
  onClose: () => void;
  onSelectIncident: (id: string) => void;
}

const COMP_LABELS = ['IDENTITY', 'CLOUD', 'ENDPOINT', 'EXPOSURE'];

const EntityDossierModal: React.FC<EntityDossierModalProps> = ({
  entity,
  onClose,
  onSelectIncident,
}) => {
  const { notify } = useToast();
  const gaugeColor = scoreColorVar(entity.score);

  return (
    <Dialog
      size="md"
      onClose={onClose}
      title={
        <>
          {entity.n}
          <span className="ml-2 text-[12px] font-bold text-c3-blue border border-c3-blue rounded px-1.5 py-0.5 align-middle">
            DOSSIER
          </span>
        </>
      }
    >
      <div className="text-c3-mute text-sm mb-4">
        {entity.type} · risk recomputed on every correlated signal · 7-day trend {entity.trend}
      </div>
      <div className="flex gap-[22px] items-center mb-4 dossier-top">
        <div
          className="w-[112px] h-[112px] rounded-full flex items-center justify-center relative flex-shrink-0"
          style={{
            background: `conic-gradient(${gaugeColor} ${entity.score * 3.6}deg, var(--s3) 0deg)`,
          }}
        >
          <div className="w-[88px] h-[88px] rounded-full bg-c3-panel flex flex-col items-center justify-center">
            <span className={`text-[28px] font-bold ${scoreCls(entity.score)}`}>{entity.score}</span>
            <span className="absolute bottom-3.5 text-c3-mute text-[12px] tracking-wider">RISK / 100</span>
          </div>
        </div>
        <div className="flex-1">
          <div className="comps gap-3.5 mb-4">
            {entity.comps.map((v, i) => (
              <div key={COMP_LABELS[i]} className="comp">
                <div className="cl">
                  {COMP_LABELS[i]} · {v}
                </div>
                <div className="cb" style={{ height: 7 }}>
                  <i style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mb-0">
            <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2">AI BRIEF</h4>
            <div className="text-sm text-c3-white leading-relaxed">{entity.brief}</div>
          </div>
        </div>
      </div>

      <div className="my-4">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">ENTITY TIMELINE (TODAY)</h4>
        {entity.tl.map((row, i) => (
          <div key={i} className="grid grid-cols-[64px_12px_1fr] gap-2.5 text-[13px] mb-0.5">
            <span className="text-c3-mute text-right">{row[0]}</span>
            <span className="relative">
              <span className="absolute left-1 top-1.5 w-2 h-2 rounded-full bg-c3-purple" />
              {i < entity.tl.length - 1 && (
                <span className="absolute left-[7px] top-[15px] bottom-[-8px] w-0.5 bg-c3-tile-bd" />
              )}
            </span>
            <span>{row[1]}</span>
          </div>
        ))}
      </div>

      <div className="my-4">
        <h4 className="text-xs font-bold text-c3-mute tracking-widest mb-2.5">LINKED INCIDENTS</h4>
        <div className="flex flex-wrap gap-2">
          {entity.rel.map((id) => (
            <button
              key={id}
              type="button"
              className="text-[13px] text-c3-lpurple bg-acc/10 border border-c3-purple rounded-md px-3 py-1 cursor-pointer"
              onClick={() => onSelectIncident(id)}
            >
              {id}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mt-5">
        <button
          type="button"
          className="c3-btn c3-btn-approve"
          onClick={() => notify({ tone: 'success', title: 'Watch list.', message: `Added ${entity.n} to watch list.` })}
        >
          Add to watch list
        </button>
        <button type="button" className="c3-btn c3-btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </Dialog>
  );
};

export default EntityDossierModal;
